import type { Application } from '@3d4medical/websdk';
import { useAnalytics } from '@completeanatomy/analytics';
import { useBridge } from '@completeanatomy/bridge';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { getModelIndex } from '../utils/getModelIndex';
import { kcGateway } from '../utils/keycloak';

import { useScreen } from './useScreen';

export const useScreenActivation = (app: Application | null) => {
  const bridge = useBridge();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { screen, loadScreen } = useScreen();
  const analytics = useAnalytics();

  const activateScreen = useCallback(async () => {
    console.log('Activating screen', screen);
    if (!app) {
      console.warn('No app instance available to activate screen');

      return;
    }

    if (screen === null || screen.id === undefined) {
      console.warn('No screen found to activate');

      return;
    }

    bridge?.emit('BridgeObjectChanged', {
      identifier: 'libraryState',
      keyPath: 'openedResources.screen',
      changes: {
        name: screen.name,
        information: screen.description,
        model_identifier: screen.model_id,
        model_version: screen.model_version || 1,
        author: {
          name: screen.user.username,
          image: screen.user.image_url,
        },
      },
    });

    const screenManager = app.getScreenManager();

    try {
      await screenManager.activateModelWithExternalScreen(
        screen.id,
        screen as any,
        true,
        true,
        true,
        true
      );
    } catch (error) {
      console.error('Failed to activate screen:', error);
      navigate('/content-unavailable');

      return;
    }

    const index = getModelIndex(screen.model_id);

    if (index !== -1) {
      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: 'selectedModelIndex',
        changes: index,
      });
    }

    analytics.log('screen_opened' as any, {
      screen_id: screen.id,
      screen_name: screen.name,
      resource_type: 'screen',
    });
  }, [analytics, app, screen, bridge, navigate]);

  const loadScreenData = useCallback(async () => {
    // Only load screen data if we're on a screens path
    const isScreensPath = location.pathname.includes('/screens/');

    if (!isScreensPath) {
      return;
    }

    const screenId = params.id;

    if (!screenId) {
      console.error('Missing screen ID in URL');
      navigate('/content-unavailable');

      return;
    }

    try {
      const token = await kcGateway.getToken();

      if (!token) {
        return;
      }

      await loadScreen(token, screenId);
    } catch (error) {
      console.error('Failed to load screen data:', error);
      navigate('/content-unavailable');
    }
  }, [loadScreen, location.pathname, params.id, navigate]);

  useEffect(() => {
    const isScreensPath = location.pathname.includes('/screens/');

    if (isScreensPath) {
      loadScreenData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!app || !screen) {
      return;
    }

    const isScreensPath = location.pathname.includes('/screens/');

    if (isScreensPath) {
      activateScreen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, app, location.pathname]);
};
